import { useState, useEffect } from "react";
import { Card, message } from "antd";
import ReactMarkdown from "react-markdown";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import StandardButton from "../components/StandardButton";

const PRINT_CSS = `
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; line-height: 1.6; max-width: 800px; margin: 2rem auto; padding: 0 1rem; color: #111; }
  h1,h2,h3,h4 { line-height: 1.25; }
  pre { background: #f5f5f5; padding: 1rem; border-radius: 6px; overflow-x: auto; }
  code { background: #f5f5f5; padding: 0.1em 0.3em; border-radius: 3px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
  pre code { background: none; padding: 0; }
  table { border-collapse: collapse; }
  th, td { border: 1px solid #ddd; padding: 0.4em 0.6em; }
  blockquote { border-left: 4px solid #c7d2fe; margin: 0; padding: 0.25rem 0 0.25rem 1rem; color: #4b5563; }
  img { max-width: 100%; }
  @media print { body { margin: 0; } }
`;

const sampleMarkdown = `# Welcome to Quto.dev Markdown Renderer

This tool supports **bold**, _italic_, \`inline code\`, and code blocks:

\`\`\`js
function greet() {
  console.log("Hello, world!");
}
\`\`\`

- Lists
- [x] Checkboxes
- [Visit Quto.dev](https://quto.dev)

Inline HTML works too — e.g. <kbd>Ctrl</kbd>+<kbd>P</kbd> to print, or a <mark>highlighted note</mark>.
`;

const RenderMarkdown = () => {
  const [input, setInput] = useState(sampleMarkdown);
  const [output, setOutput] = useState("");
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  useEffect(() => {
    const id = searchParams.get("share");
    if (id) {
      api.get(`/markdown/share/${id}`)
        .then(res => {
          const data = res.data;
          if (data.markdown) {
            setInput(data.markdown);
            setOutput(data.markdown);
          }
        })
        .catch(() => {
          toast.error(t('server_error'));
        });
    }
  }, []);

  const handleRender = () => {
    setOutput(input);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(input);
      message.success(t('copy_success'));
    } catch {
      message.error(t('failed_to_copy'));
    }
  };

  const handlePrint = () => {
    const node = document.getElementById('md-output');
    if (!node) return;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) {
      toast.error(t('print_failed'));
      return;
    }
    win.document.open();
    win.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>${t('render_markdown_name')}</title><style>${PRINT_CSS}</style></head><body>${node.innerHTML}</body></html>`,
    );
    win.document.close();
    win.focus();
    // Give the new window a tick to lay out before printing.
    setTimeout(() => {
      try { win.print(); } catch {}
    }, 250);
  };

  const handleShare = async () => {
    try {
      const response = await api.post(`/markdown/share`, { markdown: input });
      const data = response.data;
      if (data.id) {
        const shareUrl = `${window.location.origin}/tools/render-markdown?share=${data.id}`;
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t('share_success'));
      } else {
        toast.error(t('share_failed'));
      }
    } catch {
      toast.error(t('share_failed'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <textarea
              rows={6}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
              placeholder={t('render_markdown_placeholder')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />

            <StandardButton
              onClick={handleRender}
              type="secondary"
              className="w-full"
            >
              {t('render_markdown_button')}
            </StandardButton>

            {output && (
              <div className="bg-white p-4 rounded-md border border-indigo-200 prose max-w-none relative">
                <div id="md-output">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{output}</ReactMarkdown>
                </div>
                <div className="mt-2 flex gap-3 flex-wrap">
                  <StandardButton
                    onClick={handleCopy}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('copy_button')}
                  </StandardButton>
                  <StandardButton
                    onClick={handlePrint}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('print_button')}
                  </StandardButton>
                  <StandardButton
                    onClick={handleShare}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('share_button')}
                  </StandardButton>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default RenderMarkdown;